import { useEffect, useState } from 'react';
import { fetchApiBlob } from '../lib/api';

interface RecordingAudio {
  /** Object URL to feed an <audio> element, or null while loading/failed. */
  src: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Playable audio for a call recording.
 *
 * The stored recording URL points at Twilio, which serves media only to
 * requests carrying the account's HTTP Basic credentials — an <audio> tag has
 * no way to send those, so pointing it at the raw URL yields a 401 and a dead
 * 0:00 player. The backend proxies the bytes instead, and this fetches them
 * with the user's token and hands back an object URL.
 *
 * Recordings are capped at two minutes, so loading the whole file is cheap.
 */
export const useRecordingAudio = (
  callId: number | null | undefined,
  hasRecording: boolean,
): RecordingAudio => {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!callId || !hasRecording) return;

    let objectUrl: string | null = null;
    let cancelled = false;

    setLoading(true);
    setError(null);

    fetchApiBlob(`/api/inbox/call/${callId}/recording-audio/`)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Could not load the recording');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      // Clearing here rather than at the top of the effect keeps a previous
      // call's audio from lingering when the id changes, without a
      // render-phase state write.
      setSrc(null);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [callId, hasRecording]);

  return { src, loading, error };
};

export default useRecordingAudio;
